import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  HouseLine, Lightbulb, Fan, Thermometer, Lock, LockOpen,
  VideoCamera, Eye, Fire, Drop, Door, Plug, Plus, X, Trash,
  PencilSimple, Check, ArrowsOutSimple
} from '@phosphor-icons/react';

const COLORS = ['#FF6B35', '#A855F7', '#22C55E', '#3B82F6', '#F59E0B', '#10B981', '#EC4899', '#00E5FF', '#EF4444'];

const DEVICE_ICONS = {
  sensor: Eye, camera: VideoCamera, appliance: Plug,
  door: Door, motion: Eye, smoke: Fire, water_leak: Drop,
};

export default function HomeLayoutPage() {
  const { api } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [devices, setDevices] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', color: '#FF6B35', x: 0, y: 0, w: 2, h: 1 });

  const fetchRooms = useCallback(async () => {
    try {
      const res = await api.get('/control-center/rooms');
      setRooms(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchRooms(); }, [fetchRooms]);

  const fetchDevices = async (roomId) => {
    try {
      const res = await api.get(`/control-center/rooms/${roomId}/devices`);
      setDevices(res.data);
      setSelectedRoom(roomId);
    } catch (e) { console.error(e); }
  };

  const handleAdd = async () => {
    if (!form.name.trim()) return;
    try {
      await api.post('/control-center/rooms', form);
      setShowAdd(false); setForm({ name: '', color: '#FF6B35', x: 0, y: 0, w: 2, h: 1 });
      fetchRooms();
    } catch (e) { console.error(e); }
  };

  const handleUpdate = async () => {
    if (!editing) return;
    try {
      await api.put(`/control-center/rooms/${editing}`, form);
      setEditing(null); setShowAdd(false);
      fetchRooms();
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (roomId) => {
    if (!window.confirm('Delete this room?')) return;
    try {
      await api.delete(`/control-center/rooms/${roomId}`);
      if (selectedRoom === roomId) { setSelectedRoom(null); setDevices(null); }
      fetchRooms();
    } catch (e) { console.error(e); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-32">
      <div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  // Calculate grid bounds
  const maxX = Math.max(4, ...rooms.map(r => r.x + r.w));
  const maxY = Math.max(4, ...rooms.map(r => r.y + r.h));
  const cellW = 140;
  const cellH = 80;

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="home-layout-page">
      <div className="flex items-start sm:items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-heading text-xl sm:text-2xl font-bold tracking-tight">Home Layout</h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">Tap any room to view and control devices inside</p>
        </div>
        <button onClick={() => { setShowAdd(true); setEditing(null); setForm({ name: '', color: '#FF6B35', x: 0, y: 0, w: 2, h: 1 }); }}
          data-testid="add-room-btn"
          className="px-4 py-2.5 rounded-2xl bg-[#FF6B35] text-white text-sm font-semibold flex items-center gap-2 btn-glow">
          <Plus size={16} /> Add Room
        </button>
      </div>

      {/* 2.5D Isometric Floor Plan */}
      <div className="rounded-3xl p-6 overflow-auto" data-testid="floor-plan"
        style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="relative mx-auto" style={{
          width: maxX * cellW + 80,
          height: maxY * cellH + 120,
          transformStyle: 'preserve-3d',
          perspective: '1200px',
        }}>
          {/* Isometric grid base */}
          <div className="absolute inset-0" style={{
            transform: 'rotateX(30deg) rotateZ(-15deg)',
            transformOrigin: 'center center',
          }}>
            {/* Grid lines */}
            {Array.from({ length: maxY + 1 }).map((_, i) => (
              <div key={`h${i}`} className="absolute left-0 right-0" style={{ top: i * cellH, height: 1, background: 'rgba(255,255,255,0.04)' }} />
            ))}
            {Array.from({ length: maxX + 1 }).map((_, i) => (
              <div key={`v${i}`} className="absolute top-0 bottom-0" style={{ left: i * cellW, width: 1, background: 'rgba(255,255,255,0.04)' }} />
            ))}

            {/* Room blocks */}
            {rooms.map(room => {
              const isSelected = selectedRoom === room.room_id;
              return (
                <button key={room.room_id} onClick={() => fetchDevices(room.room_id)}
                  data-testid={`layout-room-${room.room_id}`}
                  className={`absolute rounded-xl transition-all duration-300 group cursor-pointer ${isSelected ? 'z-20 scale-[1.03]' : 'z-10 hover:z-20 hover:scale-[1.02]'}`}
                  style={{
                    left: room.x * cellW + 2,
                    top: room.y * cellH + 2,
                    width: room.w * cellW - 4,
                    height: room.h * cellH - 4,
                    background: `linear-gradient(135deg, ${room.color}25 0%, ${room.color}10 100%)`,
                    border: `2px solid ${isSelected ? room.color : room.color + '40'}`,
                    boxShadow: isSelected ? `0 0 20px ${room.color}30, inset 0 0 30px ${room.color}10` : `0 4px 12px ${room.color}10`,
                  }}>
                  <div className="flex flex-col items-center justify-center h-full gap-1 relative">
                    <HouseLine size={22} weight="duotone" style={{ color: room.color }} />
                    <span className="text-xs font-bold tracking-tight" style={{ color: room.color }}>{room.name}</span>
                    {/* Edit/delete on hover */}
                    <div className="absolute top-1 right-1 hidden group-hover:flex gap-1">
                      <div onClick={(e) => { e.stopPropagation(); setEditing(room.room_id); setForm({ name: room.name, color: room.color, x: room.x, y: room.y, w: room.w, h: room.h }); setShowAdd(true); }}
                        className="p-1 rounded bg-white/10 hover:bg-white/20 cursor-pointer"><PencilSimple size={10} /></div>
                      <div onClick={(e) => { e.stopPropagation(); handleDelete(room.room_id); }}
                        className="p-1 rounded bg-red-500/20 hover:bg-red-500/30 cursor-pointer"><Trash size={10} /></div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Shadow / floor effect */}
          <div className="absolute bottom-0 left-0 right-0 h-4" style={{
            background: 'linear-gradient(0deg, rgba(0,0,0,0.3) 0%, transparent 100%)',
            transform: 'rotateX(30deg) rotateZ(-15deg)',
            transformOrigin: 'bottom center',
          }} />
        </div>
      </div>

      {/* Room Device Detail */}
      {selectedRoom && devices && (
        <div className="rounded-2xl p-6" data-testid="layout-room-detail"
          style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: `${devices.room?.color}20` }}>
                <HouseLine size={18} weight="duotone" style={{ color: devices.room?.color }} />
              </div>
              <h2 className="font-heading text-lg font-bold">{devices.room?.name}</h2>
            </div>
            <button onClick={() => { setSelectedRoom(null); setDevices(null); }}
              className="p-2 rounded-xl hover:bg-white/5"><X size={18} /></button>
          </div>
          {devices.devices?.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">No devices in this room yet.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {devices.devices.map(dev => {
                const Icon = DEVICE_ICONS[dev.subtype] || DEVICE_ICONS[dev.type] || Plug;
                const isActive = ['active', 'on', 'triggered', 'locked'].includes(dev.state);
                return (
                  <div key={dev.id} className="rounded-xl p-4 flex items-center gap-3"
                    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isActive ? 'bg-[#FF6B35]/15' : 'bg-white/5'}`}>
                      <Icon size={20} weight="duotone" className={isActive ? 'text-[#FF6B35]' : 'text-neutral-500'} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{dev.name}</p>
                      <p className="text-[10px] text-muted-foreground uppercase">{dev.type} — {dev.state}</p>
                    </div>
                    <div className={`w-2 h-2 rounded-full ${isActive ? 'bg-green-400' : 'bg-neutral-600'}`} />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Room Modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)' }}>
          <div className="w-full max-w-md mx-4 rounded-3xl p-6" data-testid="room-form-modal"
            style={{ background: 'rgba(30,30,30,0.98)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <h3 className="text-lg font-bold mb-4">{editing ? 'Edit Room' : 'Add Room'}</h3>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase block mb-1">Room Name</label>
                <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  data-testid="room-name-input" placeholder="e.g., Living Room"
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50" />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase block mb-1">Color</label>
                <div className="flex gap-2 flex-wrap">
                  {COLORS.map(c => (
                    <button key={c} onClick={() => setForm(f => ({ ...f, color: c }))}
                      className={`w-8 h-8 rounded-lg transition-all ${form.color === c ? 'ring-2 ring-white scale-110' : ''}`}
                      style={{ background: c }} />
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-4 gap-3">
                {[{ key: 'x', label: 'Column' }, { key: 'y', label: 'Row' }, { key: 'w', label: 'Width' }, { key: 'h', label: 'Height' }].map(f => (
                  <div key={f.key}>
                    <label className="text-[10px] text-muted-foreground uppercase block mb-1">{f.label}</label>
                    <input type="number" min={f.key === 'w' || f.key === 'h' ? 1 : 0} max={6}
                      value={form[f.key]} onChange={e => setForm(prev => ({ ...prev, [f.key]: parseInt(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm outline-none text-center" />
                  </div>
                ))}
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => { setShowAdd(false); setEditing(null); }}
                className="flex-1 py-3 rounded-xl text-sm font-semibold text-neutral-400" style={{ background: 'rgba(255,255,255,0.05)' }}>
                Cancel
              </button>
              <button onClick={editing ? handleUpdate : handleAdd} data-testid="room-save-btn"
                className="flex-1 py-3 rounded-xl bg-[#FF6B35] text-white text-sm font-bold">
                {editing ? 'Update' : 'Add Room'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
